import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { ComponentProps, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import useResizeObserver from '@alltrails/core/hooks/useResizeObserver';
import Link from '@alltrails/denali/components/Link';
import LoadingDots from '@alltrails/denali/components/LoadingDots';
import ErrorFilled from '@alltrails/denali/icons/ErrorFilled';
import GradientLayerLegend from '@alltrails/maps/components/GradientLayerLegend/GradientLayerLegend';
import LayerLegendContainer from '@alltrails/maps/components/LayerLegendContainer/LayerLegendContainer';
import { ConditionStyleId } from '@alltrails/maps/types/Styles';
import { useGetConditionsMetadataQuery } from '../../redux/conditionsApi';
import { useAppSelector } from '../../redux/helpers';
import conditionStyleIdToConditionType from '../../utils/conditionStyleIdToConditionType';
import { createLinearGradientStyleString } from '../../utils/gradientHelpers';
import DayAndTimeControls from '../DayAndTimeControls';
import styles from './ConditionLegend.module.scss';

type ConditionLegendProps = {
  activeCondition?: Exclude<ConditionStyleId, 'groundConditions'>;
  dayAndTimeControls?: Pick<
    ComponentProps<typeof DayAndTimeControls>,
    'days' | 'intervalMinutes' | 'onDayChange' | 'onPause' | 'onPlay' | 'onTimeChange' | 'utcOffsetSeconds'
  >;
};

const ConditionLegend = ({ activeCondition, dayAndTimeControls }: ConditionLegendProps) => {
  const { displayMetric } = useAppSelector(state => ({
    activeCondition: state.conditions.activeCondition,
    displayMetric: state.context.displayMetric
  }));
  const isDailySnowDepthLegend = activeCondition === 'snowDepth' && dayAndTimeControls;

  const [legendContainer, setLegendContainer] = useState<HTMLElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const handleResize = useMemo(
    () =>
      debounce((entries: ResizeObserverEntry[]) => {
        const { width } = entries[0].target.getBoundingClientRect();
        setContainerWidth(width);
      }, 100),
    []
  );
  const [observe, disconnect] = useResizeObserver(handleResize);

  useEffect(() => {
    if (legendContainer) {
      observe(legendContainer);
    }

    return () => disconnect();
  }, [disconnect, legendContainer, observe]);

  const { data, isError, isFetching, refetch } = useGetConditionsMetadataQuery();

  const legend = useMemo(() => {
    if (activeCondition && data) {
      const conditionType = conditionStyleIdToConditionType(activeCondition);
      const metadata = conditionType ? data.find(metadata => metadata.type === conditionType) : undefined;
      if (metadata?.legends.length) {
        return metadata.legends[0];
      }
    }
    return undefined;
  }, [activeCondition, data]);

  const gradientBackgroundStyle = useMemo(() => {
    if (legend) {
      return createLinearGradientStyleString(legend.rgb);
    }
    return '';
  }, [legend]);

  const ticks = useMemo<ComponentProps<typeof GradientLayerLegend>['ticks']>(() => {
    if (legend) {
      if ('labels' in legend) {
        const labels = legend.labels!;
        return labels.map((label, index) => {
          return [index / (labels.length - 1), label];
        });
      }
      const { ticks: legendTicks, unit } = legend[displayMetric ? 'metric' : 'imperial'];

      // When the container is too narrow, we filter out every other tick to make the legend less crowded
      const filteredTicks = containerWidth < 324 && legendTicks.length > 6 ? legendTicks.filter((_, index) => index % 2 === 0) : legendTicks;

      if (activeCondition === 'snowDepth' && displayMetric) {
        // The legend gets too tight when displaying every unit, so we only should it on the first tick.
        // We also apply a space between the value and unit for metric snowDepth.
        return filteredTicks.map(([offset, value], index) => [offset, index === 0 ? `${Math.round(value)} ${unit}` : String(Math.round(value))]);
      }

      return filteredTicks.map(([offset, value]) => [offset, `${Math.round(value)}${unit}`]);
    }
    return [];
  }, [activeCondition, containerWidth, displayMetric, legend]);

  const legendContent = useMemo(() => {
    if (isFetching) {
      return <LoadingDots testId="condition-legend-loading" />;
    }
    if (isError) {
      return (
        <div className={styles.error}>
          <div className={styles.errorIconAndText}>
            <ErrorFilled color="--color-text-primary-inverse" size="sm" />
            <Typography variant="text100" color="inverse">
              <FormattedMessage defaultMessage="Unable to load map legend" />
            </Typography>
          </div>
          <Link onClick={refetch} size="sm" variant="inverse">
            <FormattedMessage defaultMessage="Retry" />
          </Link>
        </div>
      );
    }
    const gradient = <GradientLayerLegend gradientBackgroundStyle={gradientBackgroundStyle} ticks={ticks} />;

    if (isDailySnowDepthLegend) {
      return (
        <>
          <div data-at-theme-colors="dark" onAnimationEnd={e => e.stopPropagation()}>
            <DayAndTimeControls {...dayAndTimeControls} compact hideTime />
          </div>
          <div className={styles.verticalDivider} />
          {gradient}
        </>
      );
    }

    return dayAndTimeControls ? (
      <>
        {gradient}
        <div className={styles.divider} />
        <div className={styles.dayAndTimeControlsContainer} data-at-theme-colors="dark" onAnimationEnd={e => e.stopPropagation()}>
          <DayAndTimeControls {...dayAndTimeControls} compact />
        </div>
      </>
    ) : (
      gradient
    );
  }, [isFetching, isError, gradientBackgroundStyle, ticks, dayAndTimeControls, refetch, isDailySnowDepthLegend]);

  return (
    <LayerLegendContainer
      className={classNames(styles.container, {
        [styles.dailySnowDepthLegend]: isDailySnowDepthLegend
      })}
      isOpen={Boolean(activeCondition)}
      ref={setLegendContainer}
      testIdPrefix="condition-legend"
    >
      {legendContent}
    </LayerLegendContainer>
  );
};

export default ConditionLegend;
